/* ==============================================================================
   Painel de administração da instância
   ==============================================================================
   Quatro seções: visão geral, usuários, transmissões, auditoria. Mais a seção de
   varredura de conteúdo, que existe para declarar o que é possível e o que não é.

   O que este painel NÃO faz, e por que a tela diz isso em voz alta
   ==============================================================================
   Não lê mensagem de ninguém. Não entra em conversa direta. Não escuta chamada.

   Os dois primeiros são decisão de desenho; o terceiro é impossível por
   construção — voz e tela são P2P e nunca chegam ao servidor. Um painel de
   administração que não afirma seus limites deixa quem usa supondo que eles não
   existem, e quem é administrado supondo o pior.

   A telemetria é sobre QUALIDADE e PARTICIPAÇÃO
   ============================================
   Quem participou, por quanto tempo, com que RTT, jitter, perda e bitrate. É o
   que torna "a voz travou" diagnosticável. Não há conteúdo em nenhuma métrica.
   ============================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { AreaChart, BarList, useDialog } from '@concord/ui';
import { ApiError } from '@concord/core';

import type { AdminTelemetry } from '../lib/concord.ts';
import type {
  AdminOverview,
  AdminUser,
  ConcordApi,
  ScanningInfo,
  Transmission,
  UserTelemetry,
} from '../lib/concord.ts';

export interface AdminPanelProps {
  api: ConcordApi;
  onClose: () => void;
}

type Tab =
  | 'overview'
  | 'telemetry'
  | 'users'
  | 'transmissions'
  | 'audit'
  | 'scanning';

export function AdminPanel({ api, onClose }: AdminPanelProps) {
  const [tab, setTab] = useState<Tab>('overview');
  const [feedback, setFeedback] = useState<
    { kind: 'ok' | 'error'; message: string } | null
  >(null);

  const report = useCallback((kind: 'ok' | 'error', message: string) => {
    setFeedback({ kind, message });
  }, []);

  return (
    <div
      className="flex h-screen w-full"
      style={{ background: 'var(--color-surface-base)' }}
    >
      <nav
        className="flex w-56 shrink-0 flex-col gap-1 p-3"
        style={{ boxShadow: 'inset -1px 0 0 oklch(100% 0 0 / 0.055)' }}
      >
        <h1
          className="mb-0.5 px-2 font-bold"
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          Administração
        </h1>
        <p
          className="mb-2 px-2"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
        >
          da instância, não de um servidor
        </p>

        <Item active={tab === 'overview'} onClick={() => setTab('overview')}>
          Visão geral
        </Item>
        {/*
          Telemetria vem LOGO DEPOIS da visão geral, e não no fim.
          ------------------------------------------------------------------
          As duas respondem à mesma pergunta em escalas de tempo diferentes: a
          visão geral diz como está agora, a telemetria diz como chegou aqui.
          Separá-las por três abas de gestão de pessoas obrigaria a atravessar a
          lista de usuários para comparar o número de hoje com o da semana.
        */}
        <Item active={tab === 'telemetry'} onClick={() => setTab('telemetry')}>
          Telemetria
        </Item>
        <Item active={tab === 'users'} onClick={() => setTab('users')}>
          Usuários
        </Item>
        <Item active={tab === 'transmissions'} onClick={() => setTab('transmissions')}>
          Transmissões
        </Item>
        <Item active={tab === 'audit'} onClick={() => setTab('audit')}>
          Auditoria
        </Item>
        <Item active={tab === 'scanning'} onClick={() => setTab('scanning')}>
          Varredura de conteúdo
        </Item>

        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1.5 text-left"
          style={{
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          ← Voltar
        </button>
      </nav>

      <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
        {feedback !== null && (
          <p
            role="status"
            className="mb-4 px-3 py-2"
            style={{
              maxWidth: 700,
              borderRadius: 'var(--radius-md)',
              background:
                feedback.kind === 'ok'
                  ? 'var(--color-success-subtle)'
                  : 'var(--color-danger-subtle)',
              fontSize: 'var(--text-xs)',
              lineHeight: 'var(--leading-normal)',
              color:
                feedback.kind === 'ok'
                  ? 'var(--color-success)'
                  : 'var(--color-danger-on-subtle)',
            }}
          >
            {feedback.message}
          </p>
        )}

        {tab === 'overview' && <OverviewTab api={api} onReport={report} />}
        {tab === 'telemetry' && <TelemetryTab api={api} onReport={report} />}
        {tab === 'users' && <UsersTab api={api} onReport={report} />}
        {tab === 'transmissions' && <TransmissionsTab api={api} onReport={report} />}
        {tab === 'audit' && <AuditTab api={api} onReport={report} />}
        {tab === 'scanning' && <ScanningTab api={api} onReport={report} />}
      </div>
    </div>
  );
}

/* ==============================================================================
   Visão geral
============================================================================== */

function OverviewTab({
  api,
  onReport,
}: {
  api: ConcordApi;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const [data, setData] = useState<AdminOverview | null>(null);

  useEffect(() => {
    /*
     * Atualiza a cada 15 segundos, e só os números AO VIVO importam nessa
     * cadência.
     *
     * Conexões e salas ativas mudam constantemente; total de usuários não. Um
     * intervalo curto é para o primeiro grupo, e recarregar tudo junto é mais
     * simples que dois intervalos — a consulta é agregada e barata.
     */
    const load = () => {
      void api
        .adminOverview()
        .then(setData)
        .catch(() => onReport('error', 'Não foi possível carregar a visão geral.'));
    };
    load();
    const timer = setInterval(load, 15_000);
    return () => clearInterval(timer);
  }, [api, onReport]);

  if (data === null) {
    return (
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
        Carregando…
      </p>
    );
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <h2
        className="mb-4 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Visão geral
      </h2>

      <section className="mb-5">
        <h3 className="mb-2" style={sectionTitle}>
          Ao vivo
        </h3>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Stat label="Conexões" value={data.voice.liveConnections} live />
          <Stat label="Salas em chamada" value={data.voice.liveRooms} live />
          <Stat label="Em voz agora" value={data.voice.liveParticipants} live />
          <Stat label="Sessões (7 dias)" value={data.voice.sessions7d} />
        </div>
      </section>

      <section className="mb-5">
        <h3 className="mb-2" style={sectionTitle}>
          Usuários
        </h3>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Stat label="Total" value={data.users.total} />
          <Stat label="Ativos (30 dias)" value={data.users.active30d} />
          <Stat label="Administradores" value={data.users.admins} />
          <Stat label="Robôs" value={data.users.bots} />
          <Stat label="Removidos" value={data.users.deleted} />
        </div>
      </section>

      <section className="mb-5">
        <h3 className="mb-2" style={sectionTitle}>
          Conteúdo
        </h3>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Stat label="Servidores" value={data.guilds.total} />
          <Stat label="Canais de texto" value={data.channels.text} />
          <Stat label="Canais de voz" value={data.channels.voice} />
          <Stat label="Conversas diretas" value={data.channels.direct} />
          <Stat label="Mensagens" value={data.messages.total} />
          <Stat label="Mensagens (24h)" value={data.messages.last24h} />
        </div>
      </section>

      <section
        className="px-4 py-3"
        style={{
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface-raised)',
        }}
      >
        <h3 className="mb-1.5" style={sectionTitle}>
          Varredura de conteúdo
        </h3>
        <p
          style={{
            fontSize: 'var(--text-xs)',
            lineHeight: 'var(--leading-relaxed)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {data.contentScanning.reason}
        </p>
      </section>
    </div>
  );
}


/* ==============================================================================
   Telemetria
   ==============================================================================
   Como a instância chegou onde está, e onde ela está apertada.

   AS ESCOLHAS DE FORMA
   ====================
   Números de manchete são BLOCOS, não gráficos. Total de usuários é um valor
   único; um gráfico de barra com uma barra só desenha uma caixa para dizer um
   número que caberia em texto grande.

   Cadastros, mensagens e chamadas são TRÊS gráficos e não três linhas num só. As
   grandezas são de ordens diferentes — dezenas de mensagens contra unidades de
   cadastro — e a única forma de as juntar no mesmo desenho seria um segundo eixo
   vertical. Dois eixos verticais é o erro de gráfico mais comum que existe: o
   alinhamento entre as escalas é arbitrário, e o desenho passa a afirmar uma
   correlação que não está no dado.

   Os maiores servidores são barras HORIZONTAIS, porque o rótulo é um nome. Nome
   em coluna vertical vira diagonal ou trunca, e as duas saídas custam a leitura
   do rótulo — que é metade da informação.

   Uma cor só por gráfico, o acento do tema. Não há rampa por valor: colorir a
   barra maior mais escura duplica no matiz o que o comprimento já diz.

   O BLOCO DE ARMAZENAMENTO EXISTE POR UM INCIDENTE
   ================================================
   `app.sessions` chegou a 1,17 milhão de linhas para 13 usuários, e nada na
   interface indicava. O sintoma que apareceu foi outro — "a lista de usuários
   demora uma vida" — a três camadas de distância da causa.

   Uma tabela que cresce sozinha é invisível até virar lentidão. Esta lista é
   onde isso passa a ser visível ANTES.
============================================================================== */

function TelemetryTab({
  api,
  onReport,
}: {
  api: ConcordApi;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const [data, setData] = useState<AdminTelemetry | null>(null);

  useEffect(() => {
    /*
     * Uma carga, sem intervalo.
     *
     * A visão geral recarrega de 15 em 15 segundos porque tem números ao vivo.
     * Aqui não há nenhum: trinta dias de agregação não mudam enquanto se olha, e
     * um intervalo faria a consulta cara rodar centenas de vezes por hora para
     * redesenhar o mesmo gráfico.
     */
    void api
      .adminTelemetry()
      .then(setData)
      .catch(() => onReport('error', 'Não foi possível carregar a telemetria.'));
  }, [api, onReport]);

  if (data === null) {
    return (
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
        Carregando…
      </p>
    );
  }

  const soma = (campo: 'newUsers' | 'messages' | 'voiceSessions'): number =>
    data.daily.reduce((total, d) => total + d[campo], 0);

  const serie = (campo: 'newUsers' | 'messages' | 'voiceSessions') =>
    data.daily.map((d) => ({ label: d.day, value: d[campo] }));

  return (
    <div style={{ maxWidth: 860 }}>
      <h2
        className="mb-1 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Telemetria
      </h2>
      <p
        className="mb-5"
        style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
      >
        Trinta dias de uso. Só contagens — nenhum conteúdo de mensagem e nenhuma
        mídia passam por aqui.
      </p>

      {/* ====================================================================
          Manchete
          ====================================================================
          Três números e nada mais. O que se pergunta primeiro num painel destes
          é "quanto disso existe", e a resposta é um número — não um gráfico.
      ==================================================================== */}
      <section className="mb-6">
        <h3 className="mb-2" style={sectionTitle}>
          Nos últimos 30 dias
        </h3>
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
        >
          <Stat label="Novos cadastros" value={soma('newUsers')} />
          <Stat label="Mensagens enviadas" value={soma('messages')} />
          <Stat label="Chamadas iniciadas" value={soma('voiceSessions')} />
        </div>
      </section>

      {/* ====================================================================
          As três séries
      ==================================================================== */}
      <section className="mb-6 flex flex-col" style={{ gap: 'var(--spacing-5)' }}>
        <Serie titulo="Novos cadastros por dia" pontos={serie('newUsers')} />
        <Serie titulo="Mensagens por dia" pontos={serie('messages')} />
        <Serie titulo="Chamadas iniciadas por dia" pontos={serie('voiceSessions')} />
      </section>

      {/* ====================================================================
          Servidores
      ==================================================================== */}
      <section className="mb-6">
        <h3 className="mb-2" style={sectionTitle}>
          Maiores servidores
        </h3>
        <BarList
          items={data.topGuilds.map((g) => ({
            id: g.id,
            label: g.name,
            value: g.members,
            hint: `${g.channels} ${g.channels === 1 ? 'canal' : 'canais'}`,
          }))}
          formatValue={(v) => `${v.toLocaleString('pt-BR')}`}
        />
      </section>

      {/* ====================================================================
          Qualidade de chamada
          ====================================================================
          Percentis e não médias. Latência tem cauda longa: a média de 40 ms com
          um pico de 900 ms diz 62 ms, um número que não descreve nem o caso bom
          nem o ruim. A mediana diz como é normalmente; o p95 diz quão ruim fica
          quando fica.
      ==================================================================== */}
      <section className="mb-6">
        <h3 className="mb-2" style={sectionTitle}>
          Qualidade de chamada (7 dias)
        </h3>
        {data.callQuality === null || data.callQuality.samples === 0 ? (
          <p
            className="px-3 py-2"
            style={{
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-raised)',
              fontSize: 'var(--text-xs)',
              lineHeight: 'var(--leading-relaxed)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            Sem amostras no período. O cliente envia estas medidas durante as
            chamadas; se não há nenhuma, ou não houve chamada, ou a role de
            leitura do banco não está configurada nesta instância.
          </p>
        ) : (
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
          >
            <Stat
              label="Latência mediana"
              value={formatMs(data.callQuality.rttP50)}
            />
            <Stat label="Latência p95" value={formatMs(data.callQuality.rttP95)} />
            <Stat label="Jitter mediano" value={formatMs(data.callQuality.jitterP50)} />
            <Stat
              label="Perda p95"
              value={
                data.callQuality.lossP95 === null
                  ? '—'
                  : `${data.callQuality.lossP95.toFixed(1)}%`
              }
            />
            <Stat
              label="Via relay"
              value={
                data.callQuality.relayShare === null
                  ? '—'
                  : `${Math.round(data.callQuality.relayShare * 100)}%`
              }
            />
            <Stat label="Amostras" value={data.callQuality.samples} />
          </div>
        )}
      </section>

      {/* ====================================================================
          Armazenamento
      ==================================================================== */}
      <section className="mb-6">
        <h3 className="mb-2" style={sectionTitle}>
          Armazenamento — {formatBytes(data.storage.databaseBytes)} no total
        </h3>
        <BarList
          items={data.storage.tables.map((tabela) => ({
            id: tabela.name,
            label: tabela.name,
            value: tabela.bytes,
          }))}
          formatValue={formatBytes}
        />
      </section>

      {/* ====================================================================
          Instância
      ==================================================================== */}
      <section>
        <h3 className="mb-2" style={sectionTitle}>
          Instância
        </h3>
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
        >
          <Stat label="Versão" value={data.instance.version} />
          <Stat label="Node" value={data.instance.nodeVersion} />
          <Stat label="No ar há" value={formatUptime(data.instance.uptimeSeconds)} />
        </div>
      </section>
    </div>
  );
}

/**
 * Um gráfico com título e o total do período.
 *
 * O total fica no CABEÇALHO e não como rótulo em cada ponto. Um número em cima
 * de cada um dos trinta dias é ruído que ninguém lê — e o gráfico existe para
 * mostrar a FORMA da curva; o valor exato de um dia específico vem da dica ao
 * passar o cursor.
 */
function Serie({
  titulo,
  pontos,
}: {
  titulo: string;
  pontos: Array<{ label: string; value: number }>;
}) {
  const total = pontos.reduce((soma, p) => soma + p.value, 0);

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 style={sectionTitle}>{titulo}</h3>
        <span
          className="tabular-nums"
          style={{
            fontSize: 'var(--text-2xs)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {total.toLocaleString('pt-BR')} no período
        </span>
      </div>
      <AreaChart
        points={pontos}
        formatLabel={(iso) => {
          /*
           * `new Date('2026-09-01')` seria interpretado como UTC e voltaria
           * como 31/08 em qualquer fuso a oeste de Greenwich — o Brasil
           * inteiro. Fatiar a string evita a conversão que não deveria
           * acontecer: a data já vem no fuso do servidor e é só um rótulo.
           */
          const [ano, mes, dia] = iso.split('-');
          return `${dia}/${mes}/${ano}`;
        }}
      />
    </div>
  );
}

function formatMs(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(value)} ms`;
}

function formatUptime(seconds: number): string {
  const dias = Math.floor(seconds / 86_400);
  const horas = Math.floor((seconds % 86_400) / 3_600);
  const minutos = Math.floor((seconds % 3_600) / 60);
  if (dias > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${minutos}min`;
  return `${minutos}min`;
}

function formatBytes(bytes: number): string {
  /*
   * Base 1024 com os prefixos usuais.
   *
   * Rigorosamente 1024 bytes são um KiB, não um KB — mas "MiB" numa tela de
   * administração faz a pessoa parar para decifrar a unidade, e o que ela quer
   * saber é se a tabela é grande. A precisão perdida aqui é de nomenclatura, não
   * de número.
   */
  if (bytes < 1024) return `${bytes} B`;
  const unidades = ['kB', 'MB', 'GB', 'TB'];
  let valor = bytes / 1024;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024;
    i += 1;
  }
  return `${valor.toFixed(valor >= 100 ? 0 : 1)} ${unidades[i]}`;
}

/* ==============================================================================
   Usuários
============================================================================== */

function UsersTab({
  api,
  onReport,
}: {
  api: ConcordApi;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const { ask, confirm } = useDialog();

  /*
   * A senha temporaria vive em estado, e nao num aviso passageiro.
   *
   * Ela e devolvida UMA vez e nao ha como recupera-la: o banco guarda so o hash
   * Argon2id. Um `onReport` que desaparece em 8 segundos perderia a senha para
   * sempre, e a unica saida seria resetar de novo -- derrubando as sessoes da
   * pessoa outra vez.
   */
  const [tempPassword, setTempPassword] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [telemetry, setTelemetry] = useState<Record<string, UserTelemetry>>({});

  const reload = useCallback(async () => {
    try {
      setUsers(
        await api.adminListUsers({
          ...(query.trim().length > 0 ? { q: query.trim() } : {}),
          includeDeleted,
        }),
      );
    } catch {
      onReport('error', 'Não foi possível carregar os usuários.');
    }
  }, [api, query, includeDeleted, onReport]);

  useEffect(() => {
    const timer = setTimeout(() => void reload(), 300);
    return () => clearTimeout(timer);
  }, [reload]);

  const loadTelemetry = useCallback(
    async (userId: string) => {
      if (telemetry[userId] !== undefined) return;
      try {
        const data = await api.adminUserTelemetry(userId);
        setTelemetry((current) => ({ ...current, [userId]: data }));
      } catch {
        onReport('error', 'Não foi possível carregar a telemetria.');
      }
    },
    [api, telemetry, onReport],
  );

  return (
    <div style={{ maxWidth: 860 }}>
      {/* ------------------------------------------------------------------
          Senha temporária
          ------------------------------------------------------------------
          Fica no TOPO da aba, acima da busca, e só sai quando alguém a fecha.

          Não é um aviso passageiro porque ela é irrecuperável: o banco guarda
          só o hash Argon2id. Um toast de 8 segundos perderia a senha para
          sempre, e a única saída seria resetar de novo — derrubando as sessões
          da pessoa outra vez, por um erro de interface.
      ------------------------------------------------------------------ */}
      {tempPassword !== null && (
        <TempPasswordBanner
          username={tempPassword.username}
          password={tempPassword.password}
          onDismiss={() => setTempPassword(null)}
        />
      )}

      <h2
        className="mb-3 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Usuários
      </h2>

      <div className="mb-4 flex items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar por nome ou e-mail"
          className="px-3 outline-none"
          style={{
            height: 32,
            width: 280,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-sunken)',
            boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-primary)',
          }}
        />
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(event) => setIncludeDeleted(event.target.checked)}
            style={{ accentColor: 'var(--color-accent-solid)' }}
          />
          <span
            style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
          >
            incluir removidos
          </span>
        </label>
      </div>

      <ul className="flex flex-col gap-1">
        {users.map((user) => (
          <li
            key={user.id}
            className="px-3 py-2"
            style={{
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-raised)',
              opacity: user.deletedAt !== null ? 0.55 : 1,
            }}
          >
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p
                  className="flex items-center gap-1.5 truncate font-medium"
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {user.displayName ?? user.username}
                  {user.instanceAdmin && (
                    <span
                      className="font-bold uppercase"
                      style={{
                        fontSize: 9,
                        padding: '1px 4px',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-accent-subtle)',
                        color: 'var(--color-accent-on-subtle)',
                      }}
                    >
                      admin
                    </span>
                  )}
                  {user.isBot && (
                    <span
                      style={{
                        fontSize: 9,
                        color: 'var(--color-text-tertiary)',
                      }}
                    >
                      bot
                    </span>
                  )}
                  {/*
                    O selo de suspensao fica JUNTO do nome, nao numa coluna.
                    ====================================================
                    Uma coluna "situacao" a direita e lida depois do nome, do
                    e-mail e das contagens -- e a suspensao e a informacao que
                    muda o que se faz com a linha. Ao lado do nome, ela chega
                    antes de qualquer decisao.
                  */}
                  {user.suspendedAt !== null && (
                    <span
                      className="font-bold uppercase"
                      style={{
                        fontSize: 9,
                        padding: '1px 4px',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-danger-subtle)',
                        color: 'var(--color-danger-on-subtle)',
                      }}
                      title={user.suspendedReason ?? 'Conta suspensa'}
                    >
                      suspensa
                    </span>
                  )}
                  {user.mustChangePassword && (
                    <span
                      className="font-bold uppercase"
                      style={{
                        fontSize: 9,
                        padding: '1px 4px',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-warning-subtle)',
                        /*
                         * `--color-warning`, e nao `--color-warning-on-subtle`:
                         * o segundo NAO EXISTE. `var()` sem fallback para uma
                         * propriedade nao declarada invalida a declaracao
                         * inteira -- e para `color` o resultado e herdar, o que
                         * aqui daria texto cinza sobre ambar. Ja aconteceu duas
                         * vezes neste projeto (`--color-white` e `--font-scale`).
                         *
                         * O par `warning` sobre `warning-subtle` e medido pelo
                         * verificador de contraste a 4,5:1 minimo.
                         */
                        color: 'var(--color-warning)',
                      }}
                      title="Senha definida por administrador, aguardando troca"
                    >
                      senha temp
                    </span>
                  )}
                </p>
                <p
                  className="truncate"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  @{user.username} · {user.email} · {user.guildCount} servidores ·{' '}
                  {user.messageCount} mensagens · {user.activeSessions} sessões
                  {user.lastSeenAt !== null
                    ? ` · visto ${new Date(user.lastSeenAt).toLocaleDateString('pt-BR')}`
                    : ' · nunca visto'}
                </p>
                {/*
                  O MOTIVO aparece na lista, nao escondido num tooltip.
                  ==================================================
                  Quem revisa um bloqueio precisa do motivo para decidir, e
                  obrigar a passar o mouse (ou a abrir o log de auditoria, que e
                  particionado por mes e some com a retencao) transforma a
                  revisao numa investigacao.
                */}
                {user.suspendedAt !== null && (
                  <p
                    className="mt-0.5 truncate"
                    style={{
                      fontSize: 'var(--text-2xs)',
                      color: 'var(--color-danger-on-subtle)',
                    }}
                  >
                    Suspensa: {user.suspendedReason ?? 'sem motivo registrado'}
                    {user.suspendedUntil !== null
                      ? ` · até ${new Date(user.suspendedUntil).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : ' · sem prazo'}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  const next = expanded === user.id ? null : user.id;
                  setExpanded(next);
                  if (next !== null) void loadTelemetry(user.id);
                }}
                className="shrink-0 px-2 py-1"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-surface-hover)',
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {expanded === user.id ? 'fechar' : 'métricas'}
              </button>
            </div>

            {expanded === user.id && (
              <div
                className="mt-2.5 border-t pt-2.5"
                style={{ borderColor: 'var(--color-border-subtle)' }}
              >
                {telemetry[user.id] === undefined ? (
                  <p
                    style={{
                      fontSize: 'var(--text-2xs)',
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    Carregando telemetria…
                  </p>
                ) : (
                  <TelemetryDetail data={telemetry[user.id]!} />
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {/* ------------------------------------------------------------
                      Suspender / remover suspensão
                      ------------------------------------------------------------
                      Fica ANTES de "derrubar sessões" porque é a ação de
                      moderação mais frequente: derrubar sessão resolve um
                      incidente de credencial, suspender resolve um problema de
                      comportamento — e o segundo é o que aparece toda semana.
                  ------------------------------------------------------------ */}
                  {user.suspendedAt !== null ? (
                    <AdminAction
                      label="Remover suspensão"
                      onClick={async () => {
                        const proceed = await confirm({
                          title: `Liberar ${user.username}?`,
                          description:
                            'Ele volta a poder entrar. As sessões antigas ' +
                            'continuam revogadas — ele precisa fazer login de ' +
                            'novo, o que é correto: elas foram encerradas por ' +
                            'decisão de moderação.',
                          confirmLabel: 'Remover suspensão',
                        });
                        if (!proceed) return;
                        try {
                          await api.adminUnsuspendUser(user.id);
                          onReport('ok', `${user.username} foi liberado.`);
                          await reload();
                        } catch {
                          onReport('error', 'Não foi possível remover a suspensão.');
                        }
                      }}
                    />
                  ) : (
                    <AdminAction
                      label="Suspender conta"
                      danger
                      disabled={user.instanceAdmin}
                      /*
                       * Administrador não é suspenso por aqui, e o botão diz por
                       * quê em vez de só ficar cinza. Um botão desabilitado sem
                       * explicação faz a pessoa clicar várias vezes.
                       */
                      title={
                        user.instanceAdmin
                          ? 'Revogue o privilégio de administrador antes de suspender'
                          : undefined
                      }
                      onClick={async () => {
                        /*
                         * O MOTIVO é pedido primeiro, e é obrigatório na rota.
                         *
                         * Escrevê-lo é o que faz quem bloqueia reconsiderar, e é
                         * o que permite outra pessoa revisar depois. Um bloqueio
                         * sem motivo registrado é indefensável numa revisão — e a
                         * pessoa bloqueada merece saber por quê, porque a
                         * mensagem que ela recebe é este texto.
                         */
                        const reason = await ask({
                          title: `Suspender ${user.username}`,
                          description:
                            'O acesso é bloqueado e as sessões abertas caem. ' +
                            'Nada é apagado, e você pode liberar depois. Este ' +
                            'motivo é mostrado a ele ao tentar entrar.',
                          label: 'Motivo',
                          placeholder: 'Descumpriu as regras da instância',
                          maxLength: 500,
                          confirmLabel: 'Continuar',
                          validate: (value) =>
                            value.trim().length < 3
                              ? 'Descreva o motivo em pelo menos 3 caracteres.'
                              : null,
                        });
                        if (reason === null) return;

                        /*
                         * O PRAZO é uma segunda pergunta, opcional.
                         *
                         * Em dias e não em data: "7" é o que se pensa, e uma data
                         * exige converter de cabeça. Vazio = indefinido, que é o
                         * padrão razoável para quem não decidiu ainda.
                         */
                        const days = await ask({
                          title: 'Por quanto tempo?',
                          description:
                            'Em dias. Deixe vazio para suspensão sem prazo — ' +
                            'você pode liberar manualmente a qualquer momento.',
                          label: 'Dias',
                          placeholder: '7',
                          maxLength: 4,
                          confirmLabel: 'Suspender',
                          validate: (value) => {
                            if (value.trim() === '') return null;
                            const n = Number(value);
                            return Number.isInteger(n) && n > 0 && n <= 3650
                              ? null
                              : 'Use um número inteiro de dias, ou deixe vazio.';
                          },
                        });
                        if (days === null) return;

                        const until =
                          days.trim() === ''
                            ? undefined
                            : new Date(
                                Date.now() + Number(days) * 24 * 60 * 60 * 1000,
                              ).toISOString();

                        try {
                          const r = await api.adminSuspendUser(user.id, {
                            reason,
                            ...(until !== undefined ? { until } : {}),
                          });
                          onReport(
                            'ok',
                            `${user.username} suspenso. ${r.sessionsRevoked} sessão(ões) ` +
                              `revogada(s).` +
                              (r.suspendedUntil !== null
                                ? ` Até ${new Date(r.suspendedUntil).toLocaleDateString('pt-BR')}.`
                                : ' Sem prazo.'),
                          );
                          await reload();
                        } catch (cause) {
                          onReport(
                            'error',
                            cause instanceof Error && cause.message.length > 0
                              ? cause.message
                              : 'Não foi possível suspender a conta.',
                          );
                        }
                      }}
                    />
                  )}

                  {/* ---- Reset de senha ---- */}
                  <AdminAction
                    label="Redefinir senha"
                    onClick={async () => {
                      const proceed = await confirm({
                        title: `Redefinir a senha de ${user.username}?`,
                        description:
                          'Uma senha temporária é gerada e mostrada UMA vez — ' +
                          'não há como recuperá-la depois, porque o banco guarda ' +
                          'só o hash. Ela vale para uma entrada: a troca é ' +
                          'exigida. As sessões abertas caem.',
                        confirmLabel: 'Gerar senha temporária',
                        danger: true,
                      });
                      if (!proceed) return;
                      try {
                        const r = await api.adminResetPassword(user.id);
                        setTempPassword({
                          username: user.username,
                          password: r.temporaryPassword,
                        });
                        await reload();
                      } catch (cause) {
                        onReport(
                          'error',
                          cause instanceof Error && cause.message.length > 0
                            ? cause.message
                            : 'Não foi possível redefinir a senha.',
                        );
                      }
                    }}
                  />

                  {/* ---- Perfil ---- */}
                  <AdminAction
                    label="Editar perfil"
                    onClick={async () => {
                      /*
                       * Só o nome de EXIBIÇÃO é editável por aqui.
                       *
                       * `username` e `email` são identidade: o primeiro aparece
                       * em toda menção e o segundo é como a pessoa entra. Um
                       * administrador trocando qualquer dos dois num diálogo de
                       * uma linha é como alguém perde o acesso por engano — e a
                       * rota aceita os três, então o caminho existe quando for
                       * preciso, com mais cuidado do que um `ask` oferece.
                       */
                      const name = await ask({
                        title: `Nome de exibição de ${user.username}`,
                        description:
                          'É o nome que aparece nas conversas. O nome de usuário ' +
                          'e o e-mail são identidade e não se mudam por aqui.',
                        label: 'Nome de exibição',
                        placeholder: user.displayName ?? user.username,
                        maxLength: 48,
                        confirmLabel: 'Salvar',
                        validate: (value) =>
                          value.trim().length === 0
                            ? 'Use ao menos um caractere, ou cancele.'
                            : null,
                      });
                      if (name === null) return;
                      try {
                        await api.adminUpdateProfile(user.id, { displayName: name });
                        onReport('ok', 'Nome de exibição atualizado.');
                        await reload();
                      } catch (cause) {
                        onReport(
                          'error',
                          cause instanceof Error && cause.message.length > 0
                            ? cause.message
                            : 'Não foi possível atualizar o perfil.',
                        );
                      }
                    }}
                  />

                  <button
                    type="button"
                    onClick={async () => {
                      const proceed = await confirm({
                        title: `Derrubar as sessões de ${user.username}?`,
                        description:
                          'Revoga as sessões E invalida os access tokens já ' +
                          'emitidos. Sem a segunda parte eles continuariam valendo ' +
                          'por até 30 DIAS — o prazo do access token foi ampliado, ' +
                          'e "expulsei o invasor" seria falso todo esse tempo.',
                        confirmLabel: 'Derrubar sessões',
                        danger: true,
                      });
                      if (!proceed) return;
                      try {
                        const result = await api.adminRevokeSessions(user.id);
                        onReport(
                          'ok',
                          `${result.revokedSessions} sessões revogadas e tokens invalidados.`,
                        );
                        await reload();
                      } catch {
                        onReport('error', 'Não foi possível revogar as sessões.');
                      }
                    }}
                    style={actionStyle}
                  >
                    Derrubar sessões
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      const grant = !user.instanceAdmin;
                      const proceed = await confirm({
                        title: grant
                          ? `Promover ${user.username} a administrador?`
                          : `Remover o privilégio de ${user.username}?`,
                        description: grant
                          ? 'Ele passa a entrar em QUALQUER servidor para moderar, e ' +
                            'a ler a trilha de auditoria da instância. Evento de ' +
                            'auditoria de severidade alta. Não dá acesso a conversas ' +
                            'diretas nem a chamadas.'
                          : 'As sessões dele são derrubadas para a revogação valer na ' +
                            'hora, nos dois canais — HTTP e gateway.',
                        confirmLabel: grant ? 'Promover' : 'Remover privilégio',
                        danger: !grant,
                        ...(grant ? { requireTyping: user.username } : {}),
                      });
                      if (!proceed) return;
                      try {
                        await api.adminSetInstanceAdmin(user.id, grant);
                        onReport(
                          'ok',
                          grant
                            ? 'Privilégio concedido. As sessões dele foram derrubadas para valer imediatamente.'
                            : 'Privilégio revogado.',
                        );
                        await reload();
                      } catch (error) {
                        onReport(
                          'error',
                          error instanceof ApiError
                            ? error.message
                            : 'Não foi possível alterar o privilégio.',
                        );
                      }
                    }}
                    style={actionStyle}
                  >
                    {user.instanceAdmin ? 'Remover admin' : 'Tornar admin'}
                  </button>

                  {user.deletedAt === null && (
                    <button
                      type="button"
                      onClick={async () => {
                        const proceed = await confirm({
                          title: `Remover a conta de ${user.username}?`,
                          description:
                            'As MENSAGENS ficam, com autoria nula — apagar ' +
                            'destruiria o lado de outras pessoas na conversa. O ' +
                            'e-mail é anonimizado para não bloquear um cadastro ' +
                            'futuro, o que seria banimento permanente disfarçado ' +
                            'de remoção.',
                          confirmLabel: 'Remover conta',
                          danger: true,
                          requireTyping: user.username,
                        });
                        if (!proceed) return;
                        try {
                          const result = await api.adminDeleteUser(user.id);
                          onReport(
                            'ok',
                            `Conta removida. ${result.messagesPreserved} mensagens preservadas com autoria nula.`,
                          );
                          await reload();
                        } catch (error) {
                          onReport(
                            'error',
                            error instanceof ApiError
                              ? error.message
                              : 'Não foi possível remover a conta.',
                          );
                        }
                      }}
                      style={{
                        ...actionStyle,
                        background: 'var(--color-danger-subtle)',
                        color: 'var(--color-danger-on-subtle)',
                      }}
                    >
                      Remover conta
                    </button>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {users.length === 0 && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
          Nenhum usuário encontrado.
        </p>
      )}
    </div>
  );
}

function TelemetryDetail({ data }: { data: UserTelemetry }) {
  const hours = Math.floor(data.voiceSeconds / 3600);
  const minutes = Math.floor((data.voiceSeconds % 3600) / 60);

  return (
    <div>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
      >
        <Stat
          label="Tempo em voz"
          value={hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`}
        />
        <Stat label="Sessões de voz" value={data.voiceSessions} />
        <Stat label="Transmissões" value={data.transmissions} />
        <Stat
          label="RTT mediano"
          value={data.medianRttMs !== null ? `${data.medianRttMs} ms` : '—'}
        />
        <Stat
          label="Jitter mediano"
          value={data.medianJitterMs !== null ? `${data.medianJitterMs} ms` : '—'}
        />
        <Stat
          label="Perda mediana"
          value={
            data.medianPacketLossPct !== null ? `${data.medianPacketLossPct}%` : '—'
          }
        />
        <Stat
          label="Pior perda"
          value={data.worstPacketLossPct !== null ? `${data.worstPacketLossPct}%` : '—'}
        />
        <Stat
          label="Upload mediano"
          value={
            data.medianOutboundKbps !== null
              ? `${(data.medianOutboundKbps / 1000).toFixed(1)} Mbps`
              : '—'
          }
        />
      </div>

      {!data.telemetryAvailable ? (
        <p
          className="mt-2"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-warning)' }}
        >
          A telemetria de qualidade exige o pool de leitura do banco
          (DB_RO_PASSWORD). Os números de tempo em voz vêm de outra fonte e estão
          corretos.
        </p>
      ) : (
        data.sampleCount === 0 && (
          <p
            className="mt-2"
            style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
          >
            Nenhuma amostra de qualidade no período. O cliente envia a cada 10
            segundos durante uma chamada.
          </p>
        )
      )}

      <p
        className="mt-2"
        style={{
          fontSize: 'var(--text-2xs)',
          lineHeight: 'var(--leading-normal)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Medianas e não médias: uma amostra de 3000 ms num handoff de celular
        arrasta a média inteira. O pior caso é reportado separado — as duas juntas
        dizem "como costuma ser" e "quão ruim ficou".
      </p>
    </div>
  );
}

/* ==============================================================================
   Transmissões
============================================================================== */

function TransmissionsTab({
  api,
  onReport,
}: {
  api: ConcordApi;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const [items, setItems] = useState<Transmission[]>([]);
  const [onlyScreens, setOnlyScreens] = useState(false);
  const [days, setDays] = useState(7);

  useEffect(() => {
    void api
      .adminTransmissions({ days, onlyWithScreens: onlyScreens, limit: 50 })
      .then(setItems)
      .catch(() => onReport('error', 'Não foi possível carregar as transmissões.'));
  }, [api, days, onlyScreens, onReport]);

  return (
    <div style={{ maxWidth: 860 }}>
      <h2
        className="mb-1 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Transmissões
      </h2>
      <p
        className="mb-4"
        style={{
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-relaxed)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Quem participou de qual chamada, por quanto tempo, e quantas telas foram
        compartilhadas. Não há conteúdo: o servidor nunca vê a tela nem ouve a voz —
        a mídia é P2P e cifrada entre os pares.
      </p>

      <div className="mb-4 flex items-center gap-3">
        <select
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
          style={{
            height: 30,
            padding: '0 8px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-sunken)',
            boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
            fontSize: 'var(--text-2xs)',
            color: 'var(--color-text-primary)',
          }}
        >
          <option value={1}>Últimas 24 horas</option>
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
        </select>

        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={onlyScreens}
            onChange={(event) => setOnlyScreens(event.target.checked)}
            style={{ accentColor: 'var(--color-accent-solid)' }}
          />
          <span
            style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
          >
            só com compartilhamento de tela
          </span>
        </label>
      </div>

      {items.length === 0 ? (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
          Nenhuma transmissão no período. As tabelas `voice_sessions` e
          `voice_participants` só recebem dados a partir do momento em que houver
          chamadas.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <li
              key={item.voiceSessionId}
              className="px-3 py-2.5"
              style={{
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-raised)',
              }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p
                  className="min-w-0 truncate font-medium"
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {item.guildName ?? 'Sem servidor'} · {item.channelName}
                </p>
                <span
                  className="shrink-0"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {item.durationSeconds !== null
                    ? formatDuration(item.durationSeconds)
                    : 'em andamento'}
                  {item.endedAt === null ? ' · ao vivo' : ''}
                </span>
              </div>

              <p
                className="mt-0.5"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {new Date(item.startedAt).toLocaleString('pt-BR')} · pico de{' '}
                {item.peakParticipants} participantes · {item.peakScreens} telas
              </p>

              {item.participants.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {item.participants.map((participant) => (
                    <li
                      key={`${item.voiceSessionId}-${participant.userId}`}
                      className="px-2 py-0.5"
                      style={{
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--color-surface-hover)',
                        fontSize: 9,
                        color: 'var(--color-text-secondary)',
                      }}
                      title={`Entrou ${new Date(participant.joinedAt).toLocaleTimeString('pt-BR')}`}
                    >
                      {participant.username}
                      {participant.secondsPresent !== null
                        ? ` · ${formatDuration(participant.secondsPresent)}`
                        : ''}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${rest}s`;
  return `${rest}s`;
}

/* ==============================================================================
   Auditoria
============================================================================== */

function AuditTab({
  api,
  onReport,
}: {
  api: ConcordApi;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const [events, setEvents] = useState<
    Array<{
      id: number;
      occurredAt: string;
      actorKind: string;
      actorUserId: string | null;
      actorIp: string | null;
      action: string;
      targetType: string | null;
      targetId: string | null;
      guildId: string | null;
      severity: number;
      requestId: string | null;
    }>
  >([]);
  const [minSeverity, setMinSeverity] = useState(0);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  useEffect(() => {
    void api
      .adminAudit({ minSeverity, limit: 100 })
      .then((list) => {
        setEvents(list);
        setUnavailable(null);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 503) {
          setUnavailable(error.message);
          return;
        }
        onReport('error', 'Não foi possível carregar a auditoria.');
      });
  }, [api, minSeverity, onReport]);

  return (
    <div style={{ maxWidth: 900 }}>
      <h2
        className="mb-1 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Auditoria
      </h2>
      <p
        className="mb-4"
        style={{
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-relaxed)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Somente leitura. A aplicação não consegue apagar nem alterar esta tabela — a
        role `concord_app` não tem DELETE nem UPDATE em `audit`, e há teste
        automatizado que prova isso.
      </p>

      {unavailable !== null ? (
        <p
          className="px-3 py-2"
          style={{
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-warning-subtle)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-warning)',
          }}
        >
          {unavailable}
        </p>
      ) : (
        <>
          <div className="mb-3">
            <select
              value={minSeverity}
              onChange={(event) => setMinSeverity(Number(event.target.value))}
              style={{
                height: 30,
                padding: '0 8px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-sunken)',
                boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-text-primary)',
              }}
            >
              <option value={0}>Todos os eventos</option>
              <option value={1}>Aviso ou acima</option>
              <option value={2}>Alto ou acima</option>
              <option value={3}>Só crítico</option>
            </select>
          </div>

          {events.length === 0 ? (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
              Nenhum evento no filtro.
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {events.map((event) => (
                <li
                  key={event.id}
                  className="flex items-baseline gap-3 px-2.5 py-1.5"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    background:
                      event.severity >= 2
                        ? 'var(--color-warning-subtle)'
                        : 'transparent',
                  }}
                >
                  <span
                    className="shrink-0"
                    style={{
                      fontSize: 9,
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--color-text-tertiary)',
                      width: 120,
                    }}
                  >
                    {new Date(event.occurredAt).toLocaleString('pt-BR')}
                  </span>
                  <code
                    className="shrink-0"
                    style={{
                      fontSize: 'var(--text-2xs)',
                      fontFamily: 'var(--font-mono)',
                      color:
                        event.severity >= 2
                          ? 'var(--color-warning)'
                          : 'var(--color-text-secondary)',
                      minWidth: 160,
                    }}
                  >
                    {event.action}
                  </code>
                  <span
                    className="min-w-0 flex-1 truncate"
                    style={{
                      fontSize: 'var(--text-2xs)',
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    {event.actorKind}
                    {event.actorUserId !== null
                      ? ` ${event.actorUserId.slice(0, 8)}`
                      : ''}
                    {event.actorIp !== null ? ` · ${event.actorIp}` : ''}
                    {event.targetType !== null ? ` → ${event.targetType}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/* ==============================================================================
   Varredura de conteúdo
============================================================================== */

function ScanningTab({
  api,
  onReport,
}: {
  api: ConcordApi;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const [info, setInfo] = useState<ScanningInfo | null>(null);

  useEffect(() => {
    void api
      .adminScanning()
      .then(setInfo)
      .catch(() => onReport('error', 'Não foi possível carregar a informação.'));
  }, [api, onReport]);

  if (info === null) {
    return (
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
        Carregando…
      </p>
    );
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <h2
        className="mb-1 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Varredura de conteúdo
      </h2>

      <p
        className="mb-4 px-3 py-2"
        style={{
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-warning-subtle)',
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-normal)',
          color: 'var(--color-warning)',
        }}
      >
        <strong>Desativada e não implementada.</strong> Esta tela existe para
        declarar o que é tecnicamente possível, o que é impossível por construção,
        e a decisão jurídica que precede qualquer código.
      </p>

      <section className="mb-5">
        <h3 className="mb-2" style={sectionTitle}>
          O que é varrível, e o que não é
        </h3>
        <ul className="flex flex-col gap-2">
          {info.surfaces.map((surface) => (
            <li
              key={surface.surface}
              className="px-3 py-2.5"
              style={{
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-raised)',
                borderLeft: `3px solid ${
                  surface.scannable
                    ? 'var(--color-warning)'
                    : 'var(--color-success)'
                }`,
              }}
            >
              <p
                className="font-medium"
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {surface.surface}
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 9,
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-wide)',
                    color: surface.scannable
                      ? 'var(--color-warning)'
                      : 'var(--color-success)',
                  }}
                >
                  {surface.scannable ? 'varrível' : 'impossível'}
                </span>
              </p>
              <p
                className="mt-1"
                style={{
                  fontSize: 'var(--text-2xs)',
                  lineHeight: 'var(--leading-normal)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {surface.reason}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-5">
        <h3 className="mb-2" style={sectionTitle}>
          Ponto de extensão
        </h3>
        <div
          className="px-3 py-2.5"
          style={{
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-raised)',
          }}
        >
          <code
            className="block"
            style={{
              fontSize: 'var(--text-2xs)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-accent-on-subtle)',
            }}
          >
            {info.extensionPoint.hook}
          </code>
          <code
            className="mt-1.5 block break-all"
            style={{
              fontSize: 9,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1.6,
              color: 'var(--color-text-tertiary)',
            }}
          >
            {info.extensionPoint.signature}
          </code>
          <p
            className="mt-2"
            style={{
              fontSize: 'var(--text-2xs)',
              lineHeight: 'var(--leading-normal)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {info.extensionPoint.notes}
          </p>
        </div>
      </section>

      <section>
        <h3 className="mb-2" style={sectionTitle}>
          Decisão jurídica pendente
        </h3>
        <div
          className="px-3 py-2.5"
          style={{
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-subtle)',
          }}
        >
          <p
            style={{
              fontSize: 'var(--text-xs)',
              lineHeight: 'var(--leading-relaxed)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {info.legalGate.basis}
          </p>
          <p
            className="mt-2 font-medium"
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-danger-on-subtle)',
            }}
          >
            Aprovação necessária: {info.legalGate.approvals.join(', ')}
          </p>
          <p
            className="mt-1.5"
            style={{
              fontSize: 'var(--text-2xs)',
              lineHeight: 'var(--leading-normal)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            {info.legalGate.note}
          </p>
        </div>
      </section>
    </div>
  );
}

/* ==============================================================================
   Peças
============================================================================== */

const sectionTitle: React.CSSProperties = {
  fontSize: 'var(--text-2xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-wide)',
  color: 'var(--color-text-tertiary)',
};

const actionStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-hover)',
  fontSize: 'var(--text-2xs)',
  color: 'var(--color-text-secondary)',
};

function Item({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-1.5 text-left transition-colors"
      style={{
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-xs)',
        fontWeight: active ? 600 : 400,
        background: active ? 'var(--color-accent-subtle)' : 'transparent',
        color: active ? 'var(--color-accent-on-subtle)' : 'var(--color-text-secondary)',
      }}
    >
      {children}
    </button>
  );
}

function Stat({
  label,
  value,
  live = false,
}: {
  label: string;
  value: number | string;
  live?: boolean;
}) {
  return (
    <div
      className="px-3 py-2"
      style={{
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-raised)',
      }}
    >
      <p
        className="flex items-center gap-1.5"
        style={{
          fontSize: 'var(--text-lg)',
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text-primary)',
        }}
      >
        {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
        {live && (
          <span
            aria-hidden="true"
            className="block size-1.5 rounded-full"
            style={{ background: 'var(--color-success)' }}
          />
        )}
      </p>
      <p
        style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
      >
        {label}
      </p>
    </div>
  );
}

/* ==============================================================================
   Botão de ação do painel
   ==============================================================================
   Existe porque as ações de moderação de conta já são cinco, e cada uma repetia
   dez linhas de `style` inline. O que importa aqui não é a economia de linhas —
   é que um botão destrutivo e um neutro passem a ser DISTINGUÍVEIS por
   construção, em vez de depender de alguém lembrar de pintar o vermelho.

   `disabled` com `title` em vez de escondido: um botão que desaparece faz a
   pessoa procurar a ação; um cinza com explicação responde a pergunta.
============================================================================== */

function AdminAction({
  label,
  onClick,
  danger = false,
  disabled = false,
  title,
}: {
  label: string;
  onClick: () => void | Promise<void>;
  danger?: boolean;
  disabled?: boolean;
  title?: string | undefined;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      {...(title !== undefined ? { title } : {})}
      onClick={() => void onClick()}
      className="shrink-0 px-2.5 py-1 font-medium"
      style={{
        borderRadius: 'var(--radius-md)',
        background: disabled
          ? 'var(--color-surface-sunken)'
          : danger
            ? 'var(--color-danger-subtle)'
            : 'var(--color-surface-hover)',
        color: disabled
          ? 'var(--color-text-disabled)'
          : danger
            ? 'var(--color-danger-on-subtle)'
            : 'var(--color-text-secondary)',
        fontSize: 'var(--text-2xs)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

/* ==============================================================================
   Faixa da senha temporária
   ==============================================================================
   O único momento em que esta senha existe em texto claro. Depois de fechada,
   ninguém — nem quem administra a instância — consegue recuperá-la.

   Por isso três coisas:
     · ela é SELECIONÁVEL (`<input readOnly>` e não `<span>`), porque
       `navigator.clipboard` exige contexto seguro e pode ser negado, e Ctrl+C
       sempre funciona;
     · o botão de fechar diz o que fechar significa;
     · o texto instrui a repassá-la por um canal privado, porque o reflexo é
       colar no primeiro chat aberto.
============================================================================== */

function TempPasswordBanner({
  username,
  password,
  onDismiss,
}: {
  username: string;
  password: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      role="status"
      className="mb-4 p-3.5"
      style={{
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-warning-subtle)',
        boxShadow: 'inset 0 0 0 1px var(--color-warning)',
      }}
    >
      <p
        className="font-semibold"
        style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}
      >
        Senha temporária de @{username}
      </p>
      <p
        className="mt-1"
        style={{
          fontSize: 'var(--text-2xs)',
          lineHeight: 'var(--leading-relaxed)',
          color: 'var(--color-text-secondary)',
        }}
      >
        Copie agora — ela não pode ser recuperada depois. Repasse por um canal
        privado. Ela vale para uma entrada: a troca é exigida no primeiro acesso,
        e as sessões antigas já foram derrubadas.
      </p>

      <div className="mt-2.5 flex items-center gap-2">
        <input
          readOnly
          value={password}
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 px-2.5 py-2"
          style={{
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-sunken)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-sm)',
            letterSpacing: '0.08em',
            border: '1px solid var(--color-border-default)',
          }}
        />
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(password);
              setCopied(true);
              // Volta ao normal: "copiado" permanente mentiria depois de a
              // pessoa copiar outra coisa em outro programa.
              setTimeout(() => setCopied(false), 2000);
            } catch {
              // Sem permissão de área de transferência o campo continua
              // selecionável — Ctrl+C resolve.
              setCopied(false);
            }
          }}
          className="shrink-0 px-3 py-2 font-semibold"
          style={{
            borderRadius: 'var(--radius-md)',
            background: copied
              ? 'var(--color-success-subtle)'
              : 'var(--color-accent-solid)',
            color: copied ? 'var(--color-success)' : 'var(--color-on-solid)',
            fontSize: 'var(--text-2xs)',
            whiteSpace: 'nowrap',
          }}
        >
          {copied ? 'Copiado' : 'Copiar'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          title="Fechar. A senha não poderá mais ser vista."
          className="shrink-0 px-3 py-2"
          style={{
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text-tertiary)',
            fontSize: 'var(--text-2xs)',
            whiteSpace: 'nowrap',
          }}
        >
          Já copiei
        </button>
      </div>
    </div>
  );
}
